import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { RouterLink } from '../../utils/linkNav';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Snackbar,
  Autocomplete,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ShoppingCart,
  Assignment,
  Assessment,
  People,
  ReceiptLong,
  AccountBalance,
  Payment,
  Download,
  Add,
  Visibility,
  Edit,
  CheckCircle,
  Close,
} from '@mui/icons-material';
import VendorManagementPage from './VendorPage';
import PurchaseOrderPage from './PurchaseOrderPage';
import VendorPaymentPage from './VendorPaymentPage';
import VendorOnboardingPage from './VendorOnboardingPage';
import VendorOnboardingDetailPage from './VendorOnboardingDetailPage';
import GoodsReceiptPage from './GoodsReceiptPage';
import GoodsReceiptFormPage from './GoodsReceiptFormPage';
import MatchWorkbenchPage from './MatchWorkbenchPage';
import MaterialRequirementsControl from './MaterialRequirementsControl';
import SupplierInvoiceNewPage from './SupplierInvoiceNewPage';
import PurchaseInvoiceDetailPage from './PurchaseInvoiceDetailPage';
import VendorPaymentNewPage from './VendorPaymentNewPage';
import ProcurementCommandCenter from './ProcurementCommandCenter';
import ProcurementExceptionCenter from './ProcurementExceptionCenter';
import VendorDetailPage from './VendorDetailPage';
import PurchaseRequisitionPage from './PurchaseRequisitionPage';
import PurchaseRequisitionFormPage from './PurchaseRequisitionFormPage';
import PurchaseRequisitionDetailPage from './PurchaseRequisitionDetailPage';
import PurchaseOrderDetailPage from './PurchaseOrderDetailPage';
import GoodsReceiptDetailPage from './GoodsReceiptDetailPage';
import VendorPaymentDetailPage from './VendorPaymentDetailPage';
import RfqPage from './RfqPage';
import RfqFormPage from './RfqFormPage';
import RfqDetailPage from './RfqDetailPage';
import PaymentProposalPage from './PaymentProposalPage';
import ProcurementAnalyticsPage from './ProcurementAnalyticsPage';
import ServiceEntryPage from './ServiceEntryPage';
import ServiceEntryFormPage from './ServiceEntryFormPage';
import ServiceEntryDetailPage from './ServiceEntryDetailPage';
import ServiceWorkOrderPage from './ServiceWorkOrderPage';
import ServiceWorkOrderFormPage from './ServiceWorkOrderFormPage';
import ServiceWorkOrderDetailPage from './ServiceWorkOrderDetailPage';
import ServiceBillPassingPage from './ServiceBillPassingPage';
import { ErrorState } from '../../components/common/pageKit';

const TDSManagement: React.FC = () => {
  const navigate = useNavigate();
  const [tdsRecords, setTdsRecords] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [loadError, setLoadError] = React.useState(false);
  const [tdsStats, setTdsStats] = React.useState({
    totalDeductions: 0,
    totalInvoiceAmount: 0,
    pendingCertificates: 0,
    certificatesGenerated: 0,
    totalTransactions: 0
  });

  React.useEffect(() => {
    fetchTDSDeductions();
  }, []);

  const fetchTDSDeductions = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const { api } = await import('../../services/apiClient');
      const response = await api.get<any>('/procurement/tds-deductions');
      setTdsRecords(response.deductions || []);
      setTdsStats(response.summary || {
        totalDeductions: 0,
        totalInvoiceAmount: 0,
        pendingCertificates: 0,
        certificatesGenerated: 0,
        totalTransactions: 0
      });
    } catch (err) {
      console.error('Failed to fetch TDS deductions:', err);
      setTdsRecords([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCertificate = async (id: string) => {
    try {
      const { api } = await import('../../services/apiClient');
      await api.post(`/procurement/tds-deductions/${id}/generate-certificate`);
      fetchTDSDeductions();
    } catch (err) {
      setError('Failed to generate certificate');
    }
  };

  const handleDownloadCertificate = async (id: string, certificateNumber: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
      const response = await fetch(`${apiUrl}/procurement/tds-deductions/${id}/certificate`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download certificate');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TDS_Certificate_${certificateNumber.replace(/\//g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download certificate');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CERTIFICATE_GENERATED': return 'success';
      case 'DEDUCTED': return 'info';
      case 'PENDING_CERTIFICATE': return 'warning';
      default: return 'default';
    }
  };

  // Calculate quarterly TDS data from records
  const calculateQuarterlyData = React.useMemo(() => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1; // 1-12
    const currentYear = today.getFullYear();

    // Determine current financial year
    const fyStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;
    const fyEndYear = fyStartYear + 1;
    const fyString = `FY ${fyStartYear}-${fyEndYear.toString().slice(-2)}`;

    // Define quarters with their date ranges and due dates
    const quarters = [
      {
        label: 'Q1',
        name: `Q1 ${fyString}`,
        startMonth: 4, endMonth: 6, // Apr-Jun
        dueDate: `31-Jul-${fyStartYear.toString().slice(-2)}`,
        dueDateObj: new Date(fyStartYear, 6, 31) // July 31
      },
      {
        label: 'Q2',
        name: `Q2 ${fyString}`,
        startMonth: 7, endMonth: 9, // Jul-Sep
        dueDate: `31-Oct-${fyStartYear.toString().slice(-2)}`,
        dueDateObj: new Date(fyStartYear, 9, 31) // Oct 31
      },
      {
        label: 'Q3',
        name: `Q3 ${fyString}`,
        startMonth: 10, endMonth: 12, // Oct-Dec
        dueDate: `31-Jan-${fyEndYear.toString().slice(-2)}`,
        dueDateObj: new Date(fyEndYear, 0, 31) // Jan 31
      },
      {
        label: 'Q4',
        name: `Q4 ${fyString}`,
        startMonth: 1, endMonth: 3, // Jan-Mar
        dueDate: `31-May-${fyEndYear.toString().slice(-2)}`,
        dueDateObj: new Date(fyEndYear, 4, 31) // May 31
      }
    ];

    // Determine current quarter based on current month
    let currentQuarter = 4; // Default to Q4
    if (currentMonth >= 4 && currentMonth <= 6) currentQuarter = 1;
    else if (currentMonth >= 7 && currentMonth <= 9) currentQuarter = 2;
    else if (currentMonth >= 10 && currentMonth <= 12) currentQuarter = 3;
    else if (currentMonth >= 1 && currentMonth <= 3) currentQuarter = 4;

    // Calculate TDS amount for each quarter from tdsRecords
    const quarterlyData = quarters.map((quarter, index) => {
      const quarterNumber = index + 1;

      // Filter records for this quarter
      const quarterRecords = tdsRecords.filter(record => {
        if (!record.deduction_date) return false;
        const deductionDate = new Date(record.deduction_date);
        const deductionMonth = deductionDate.getMonth() + 1;
        const deductionYear = deductionDate.getFullYear();

        // Check if deduction falls in this quarter of the current FY
        if (quarterNumber <= 3) {
          // Q1, Q2, Q3 are in fyStartYear
          return deductionYear === fyStartYear &&
                 deductionMonth >= quarter.startMonth &&
                 deductionMonth <= quarter.endMonth;
        } else {
          // Q4 is in fyEndYear (Jan-Mar)
          return deductionYear === fyEndYear &&
                 deductionMonth >= quarter.startMonth &&
                 deductionMonth <= quarter.endMonth;
        }
      });

      // Sum TDS amounts for this quarter
      const tdsAmount = quarterRecords.reduce((sum, record) => {
        return sum + parseFloat(record.tds_amount || 0);
      }, 0);

      // Determine status
      let status: 'filed' | 'in_progress' | 'pending';
      let statusColor: 'success.main' | 'warning.main' | 'text.secondary';
      let statusText: string;

      if (quarterNumber < currentQuarter) {
        // Past quarters - assume filed
        status = 'filed';
        statusColor = 'success.main';
        statusText = 'Filed';
      } else if (quarterNumber === currentQuarter) {
        // Current quarter - in progress
        status = 'in_progress';
        statusColor = 'warning.main';
        statusText = `Due ${quarter.dueDate}`;
      } else {
        // Future quarters - pending
        status = 'pending';
        statusColor = 'text.secondary';
        statusText = 'Pending';
      }

      return {
        ...quarter,
        quarterNumber,
        tdsAmount,
        recordCount: quarterRecords.length,
        status,
        statusColor,
        statusText
      };
    });

    return quarterlyData;
  }, [tdsRecords]);

  const formatStatus = (status: string) => {
    switch (status) {
      case 'CERTIFICATE_GENERATED': return 'Certificate Generated';
      case 'DEDUCTED': return 'TDS Deducted';
      case 'PENDING_CERTIFICATE': return 'Pending Certificate';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleExportTDSData = async () => {
    try {
      const { api } = await import('../../services/apiClient');
      const response = await api.get<any>('/procurement/tds-deductions/export', {
        responseType: 'blob'
      });
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tds_deductions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to export TDS data:', err);
      setError('Failed to export TDS data');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            TDS Management - Income Tax Compliance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            TDS deductions are auto-recorded when you create vendor payments with TDS-applicable vendors.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Payment />}
            onClick={() => navigate('/procurement/payments')}
          >
            Create Payment with TDS
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExportTDSData}
          >
            Export TDS Data
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* TDS Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ReceiptLong color="primary" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="primary.main">
                    ₹{(tdsStats.totalDeductions / 1000).toFixed(0)}K
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total TDS Deducted
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assignment color="success" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {tdsStats.certificatesGenerated}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Certificates Generated
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assessment color="warning" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {tdsStats.pendingCertificates}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Certificates
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <People color="info" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    {tdsStats.totalTransactions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Transactions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* TDS Records Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            TDS Deduction Records
          </Typography>
          {loadError ? (
            <ErrorState onRetry={fetchTDSDeductions} />
          ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vendor Details</TableCell>
                <TableCell>Invoice Info</TableCell>
                <TableCell>TDS Section</TableCell>
                <TableCell align="right">Invoice Amount</TableCell>
                <TableCell align="right">TDS Amount</TableCell>
                <TableCell>Certificate No</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tdsRecords.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {record.vendor_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PAN: {record.vendor_pan}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {record.invoice_no}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {record.invoice_date && new Date(record.invoice_date).toLocaleDateString('en-IN')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Section {record.tds_section}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Rate: {record.tds_rate}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      ₹{(record.invoice_amount || 0).toLocaleString('en-IN')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      ₹{(record.tds_amount || 0).toLocaleString('en-IN')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {record.certificate_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatStatus(record.status)}
                      color={getStatusColor(record.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {record.status !== 'CERTIFICATE_GENERATED' ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => handleGenerateCertificate(record.id)}
                      >
                        Generate Certificate
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="text"
                        color="success"
                        startIcon={<Download />}
                        onClick={() => handleDownloadCertificate(record.id, record.certificate_number)}
                      >
                        Download
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {tdsRecords.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No TDS deductions found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>

      {/* Section-wise TDS Summary */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Section-wise TDS Summary (Current Financial Year)
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" color="primary" fontWeight="bold">
                    Section 194C - Contractors
                  </Typography>
                  <Typography variant="h5" color="text.primary">
                    ₹{tdsRecords.filter(r => r.tds_section === '194C').reduce((sum, r) => sum + parseFloat(r.tds_amount || 0), 0).toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tdsRecords.filter(r => r.tds_section === '194C').length} transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" color="primary" fontWeight="bold">
                    Section 194J - Professional Services
                  </Typography>
                  <Typography variant="h5" color="text.primary">
                    ₹{tdsRecords.filter(r => r.tds_section === '194J').reduce((sum, r) => sum + parseFloat(r.tds_amount || 0), 0).toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tdsRecords.filter(r => r.tds_section === '194J').length} transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" color="primary" fontWeight="bold">
                    Section 194O - E-commerce
                  </Typography>
                  <Typography variant="h5" color="text.primary">
                    ₹{tdsRecords.filter(r => r.tds_section === '194O').reduce((sum, r) => sum + parseFloat(r.tds_amount || 0), 0).toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tdsRecords.filter(r => r.tds_section === '194O').length} transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Quarterly Returns */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Quarterly TDS Returns (Form 24Q/26Q)
          </Typography>
          <Grid container spacing={2}>
            {calculateQuarterlyData.map((quarter) => (
              <Grid key={quarter.label} size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      {quarter.name}
                    </Typography>
                    <Typography variant="h6" color={quarter.statusColor}>
                      {quarter.statusText}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      TDS: ₹{quarter.tdsAmount.toLocaleString('en-IN')}
                    </Typography>
                    {quarter.recordCount > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {quarter.recordCount} deduction{quarter.recordCount !== 1 ? 's' : ''}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

const PurchaseInvoices: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = React.useState<any[]>([]);
  const [vendors, setVendors] = React.useState<any[]>([]);
  const [purchaseOrders, setPurchaseOrders] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [loadError, setLoadError] = React.useState(false);
  const [invoiceStats, setInvoiceStats] = React.useState({
    totalInvoices: 0,
    pendingApproval: 0,
    approved: 0,
    totalAmount: 0,
    pendingPayment: 0
  });

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [viewDialogOpen, setViewDialogOpen] = React.useState(false);
  const [selectedInvoice, setSelectedInvoice] = React.useState<any>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Form state
  const [formData, setFormData] = React.useState({
    vendorId: '',
    purchaseOrderId: '',
    invoiceNumber: '',
    vendorInvoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    subtotal: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    totalAmount: 0,
    remarks: ''
  });

  React.useEffect(() => {
    fetchInvoices();
    fetchVendors();
    fetchPurchaseOrders();
  }, []);

  // Auto-calculate total when amounts change
  React.useEffect(() => {
    const total = Number(formData.subtotal) + Number(formData.cgstAmount) + Number(formData.sgstAmount) + Number(formData.igstAmount);
    setFormData(prev => ({ ...prev, totalAmount: total }));
  }, [formData.subtotal, formData.cgstAmount, formData.sgstAmount, formData.igstAmount]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const { api } = await import('../../services/apiClient');
      const response = await api.get<any>('/procurement/purchase-invoices');
      setInvoices(response.invoices || []);
      setInvoiceStats(response.summary || {
        totalInvoices: 0,
        pendingApproval: 0,
        approved: 0,
        totalAmount: 0,
        pendingPayment: 0
      });
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setInvoices([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const { api } = await import('../../services/apiClient');
      const response = await api.get<any>('/procurement/vendors');
      setVendors(response.vendors || []);
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const { api } = await import('../../services/apiClient');
      const response = await api.get<any>('/procurement/purchase-orders');
      setPurchaseOrders(response.purchaseOrders || []);
    } catch (err) {
      console.error('Failed to fetch purchase orders:', err);
    }
  };

  // Dynamic Three-Way Matching calculation
  const threeWayMatching = React.useMemo(() => {
    const totalInvoices = invoices.length;
    if (totalInvoices === 0) return { poMatched: 0, grnMatched: 0, verification: 0 };

    const withPO = invoices.filter(inv => inv.po_number).length;
    const approved = invoices.filter(inv => inv.status === 'APPROVED').length;

    return {
      poMatched: Math.round((withPO / totalInvoices) * 100),
      grnMatched: Math.round((withPO / totalInvoices) * 100), // Using PO as proxy for GRN
      verification: Math.round((approved / totalInvoices) * 100)
    };
  }, [invoices]);

  // Dynamic GST & TDS Compliance calculation
  const compliance = React.useMemo(() => {
    const totalInvoices = invoices.length;
    if (totalInvoices === 0) return { gstinValidation: 0, tdsCalculation: 100, hsnVerification: 100 };

    const withGSTIN = invoices.filter(inv => inv.vendor_gstin).length;

    return {
      gstinValidation: Math.round((withGSTIN / totalInvoices) * 100),
      tdsCalculation: 100,
      hsnVerification: 100
    };
  }, [invoices]);

  const getComplianceColor = (percentage: number) => {
    if (percentage >= 90) return 'success';
    if (percentage >= 70) return 'warning';
    return 'error';
  };

  const handleCreateInvoice = async () => {
    try {
      const { api } = await import('../../services/apiClient');
      await api.post('/procurement/purchase-invoices', {
        vendorId: formData.vendorId,
        purchaseOrderId: formData.purchaseOrderId || null,
        invoiceNo: formData.invoiceNumber,
        vendorInvoiceNumber: formData.vendorInvoiceNumber,
        invoiceDate: formData.invoiceDate,
        dueDate: formData.dueDate || null,
        subtotal: formData.subtotal,
        cgstAmount: formData.cgstAmount,
        sgstAmount: formData.sgstAmount,
        igstAmount: formData.igstAmount,
        totalAmount: formData.totalAmount,
        remarks: formData.remarks
      });
      setSnackbar({ open: true, message: 'Invoice created successfully', severity: 'success' });
      setCreateDialogOpen(false);
      resetForm();
      fetchInvoices();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to create invoice', severity: 'error' });
    }
  };

  const handleUpdateInvoice = async () => {
    if (!selectedInvoice) return;
    try {
      const { api } = await import('../../services/apiClient');
      await api.put(`/procurement/purchase-invoices/${selectedInvoice.id}`, {
        vendorId: formData.vendorId,
        purchaseOrderId: formData.purchaseOrderId || null,
        invoiceNo: formData.invoiceNumber,
        vendorInvoiceNumber: formData.vendorInvoiceNumber,
        invoiceDate: formData.invoiceDate,
        dueDate: formData.dueDate || null,
        subtotal: formData.subtotal,
        cgstAmount: formData.cgstAmount,
        sgstAmount: formData.sgstAmount,
        igstAmount: formData.igstAmount,
        totalAmount: formData.totalAmount,
        remarks: formData.remarks
      });
      setSnackbar({ open: true, message: 'Invoice updated successfully', severity: 'success' });
      setCreateDialogOpen(false);
      setIsEditing(false);
      resetForm();
      fetchInvoices();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to update invoice', severity: 'error' });
    }
  };

  const handleApproveInvoice = async (id: number) => {
    try {
      const { api } = await import('../../services/apiClient');
      await api.post(`/procurement/purchase-invoices/${id}/approve`);
      setSnackbar({ open: true, message: 'Invoice approved successfully', severity: 'success' });
      fetchInvoices();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to approve invoice', severity: 'error' });
    }
  };

  const handleViewInvoice = (invoice: any) => {
    // Drill into the full invoice detail/360 workspace.
    navigate(`/procurement/invoices/${invoice.id}`);
  };

  const handleEditInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setFormData({
      vendorId: invoice.vendor_id?.toString() || '',
      purchaseOrderId: invoice.po_id?.toString() || '',
      invoiceNumber: invoice.invoice_no || '',
      vendorInvoiceNumber: invoice.vendor_invoice_number || '',
      invoiceDate: invoice.invoice_date ? new Date(invoice.invoice_date).toISOString().split('T')[0] : '',
      dueDate: invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '',
      subtotal: invoice.gross_amount || 0,
      cgstAmount: invoice.cgst_amount || 0,
      sgstAmount: invoice.sgst_amount || 0,
      igstAmount: invoice.igst_amount || 0,
      totalAmount: invoice.total_amount || 0,
      remarks: invoice.remarks || ''
    });
    setIsEditing(true);
    setCreateDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      vendorId: '',
      purchaseOrderId: '',
      invoiceNumber: '',
      vendorInvoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      subtotal: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      totalAmount: 0,
      remarks: ''
    });
    setSelectedInvoice(null);
    setIsEditing(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'PENDING_APPROVAL': return 'warning';
      case 'UNDER_REVIEW': return 'info';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'PAID': return 'success';
      case 'PROCESSING': return 'info';
      case 'PENDING': return 'warning';
      case 'ON_HOLD': return 'error';
      default: return 'default';
    }
  };

  const formatStatus = (status: string) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || status;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Create Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Purchase Invoices - GST & TDS Compliance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete invoice management with automatic TDS calculations and GST integration.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/procurement/invoices/new"
        >
          Create Invoice
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Invoice Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assignment color="primary" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="primary.main">
                    {invoiceStats.totalInvoices}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Invoices
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assessment color="warning" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {invoiceStats.pendingApproval}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Approval
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assignment color="success" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {invoiceStats.approved}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Approved
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalance color="info" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    ₹{(invoiceStats.totalAmount / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Value
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Invoice Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Invoice Management Dashboard
          </Typography>
          {loadError ? (
            <ErrorState onRetry={fetchInvoices} />
          ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice Details</TableCell>
                <TableCell>Vendor Info</TableCell>
                <TableCell>PO Reference</TableCell>
                <TableCell align="right">Amount Breakdown</TableCell>
                <TableCell align="right">Net Amount</TableCell>
                <TableCell>Invoice Status</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {invoice.invoice_no}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Date: {invoice.invoice_date && new Date(invoice.invoice_date).toLocaleDateString('en-IN')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Due: {invoice.due_date && new Date(invoice.due_date).toLocaleDateString('en-IN')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {invoice.vendor_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        GSTIN: {invoice.vendor_gstin || '-'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {invoice.po_number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Gross: ₹{(invoice.gross_amount || 0).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        GST: ₹{(invoice.gst_amount || 0).toLocaleString('en-IN')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        TDS: -₹{(invoice.tds_amount || 0).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      ₹{(invoice.net_amount || 0).toLocaleString('en-IN')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatStatus(invoice.status)}
                      color={getStatusColor(invoice.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatStatus(invoice.payment_status)}
                      color={getPaymentStatusColor(invoice.payment_status) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewInvoice(invoice)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {invoice.status === 'PENDING_APPROVAL' && (
                        <Tooltip title="Approve Invoice">
                          <IconButton size="small" color="success" onClick={() => handleApproveInvoice(invoice.id)}>
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {invoice.status !== 'APPROVED' && (
                        <Tooltip title="Edit Invoice">
                          <IconButton size="small" onClick={() => handleEditInvoice(invoice)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {invoices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No purchase invoices found. Click "Create Invoice" to add one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>

      {/* Three-Way Matching & Compliance - DYNAMIC */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Three-Way Matching Status
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2">Purchase Orders Matched</Typography>
                      <Chip label={`${threeWayMatching.poMatched}%`} color={getComplianceColor(threeWayMatching.poMatched) as any} size="small" />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2">Goods Receipt Notes Matched</Typography>
                      <Chip label={`${threeWayMatching.grnMatched}%`} color={getComplianceColor(threeWayMatching.grnMatched) as any} size="small" />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2">Invoice Verification Complete</Typography>
                      <Chip label={`${threeWayMatching.verification}%`} color={getComplianceColor(threeWayMatching.verification) as any} size="small" />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                GST & TDS Compliance
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2">GSTIN Validation</Typography>
                      <Chip label={`${compliance.gstinValidation}%`} color={getComplianceColor(compliance.gstinValidation) as any} size="small" />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2">TDS Calculations</Typography>
                      <Chip label={`${compliance.tdsCalculation}%`} color={getComplianceColor(compliance.tdsCalculation) as any} size="small" />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2">HSN Code Verification</Typography>
                      <Chip label={`${compliance.hsnVerification}%`} color={getComplianceColor(compliance.hsnVerification) as any} size="small" />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create/Edit Invoice Dialog */}
      <Dialog open={createDialogOpen} onClose={() => { setCreateDialogOpen(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{isEditing ? 'Edit Invoice' : 'Create New Invoice'}</Typography>
            <IconButton onClick={() => { setCreateDialogOpen(false); resetForm(); }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={vendors}
                getOptionLabel={(option) => option.company_name || ''}
                value={vendors.find(v => v.id?.toString() === formData.vendorId) || null}
                onChange={(_, newValue) => setFormData({ ...formData, vendorId: newValue?.id?.toString() || '', purchaseOrderId: '' })}
                renderInput={(params) => <TextField {...params} label="Vendor *" fullWidth />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={purchaseOrders.filter(po => !formData.vendorId || po.vendor_id?.toString() === formData.vendorId)}
                getOptionLabel={(option) => option.po_number || ''}
                value={purchaseOrders.find(p => p.id?.toString() === formData.purchaseOrderId) || null}
                onChange={(_, newValue) => setFormData({ ...formData, purchaseOrderId: newValue?.id?.toString() || '' })}
                disabled={!formData.vendorId}
                renderInput={(params) => <TextField {...params} label="Purchase Order (Optional)" helperText={!formData.vendorId ? "Select vendor first" : ""} fullWidth />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Invoice Number *"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Vendor Invoice Number"
                value={formData.vendorInvoiceNumber}
                onChange={(e) => setFormData({ ...formData, vendorInvoiceNumber: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Invoice Date *"
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Due Date"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Amount Details
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Subtotal *"
                type="number"
                value={formData.subtotal}
                onChange={(e) => setFormData({ ...formData, subtotal: Number(e.target.value) })}
                fullWidth
                slotProps={{ input: { startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="CGST Amount"
                type="number"
                value={formData.cgstAmount}
                onChange={(e) => setFormData({ ...formData, cgstAmount: Number(e.target.value), igstAmount: 0 })}
                fullWidth
                disabled={formData.igstAmount > 0}
                helperText={formData.igstAmount > 0 ? "Clear IGST to use CGST/SGST" : "For intra-state sales"}
                slotProps={{ input: { startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="SGST Amount"
                type="number"
                value={formData.sgstAmount}
                onChange={(e) => setFormData({ ...formData, sgstAmount: Number(e.target.value), igstAmount: 0 })}
                fullWidth
                disabled={formData.igstAmount > 0}
                helperText={formData.igstAmount > 0 ? "Clear IGST to use CGST/SGST" : "For intra-state sales"}
                slotProps={{ input: { startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="IGST Amount"
                type="number"
                value={formData.igstAmount}
                onChange={(e) => setFormData({ ...formData, igstAmount: Number(e.target.value), cgstAmount: 0, sgstAmount: 0 })}
                fullWidth
                disabled={formData.cgstAmount > 0 || formData.sgstAmount > 0}
                helperText={formData.cgstAmount > 0 || formData.sgstAmount > 0 ? "Clear CGST/SGST to use IGST" : "For inter-state sales"}
                slotProps={{ input: { startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Total Amount"
                type="number"
                value={formData.totalAmount}
                fullWidth
                disabled
                slotProps={{ input: { startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Remarks"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialogOpen(false); resetForm(); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={isEditing ? handleUpdateInvoice : handleCreateInvoice}
            disabled={!formData.vendorId || !formData.invoiceNumber || !formData.invoiceDate}
          >
            {isEditing ? 'Update Invoice' : 'Create Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Invoice Detail Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Invoice Details</Typography>
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedInvoice && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Invoice Number</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedInvoice.invoice_no}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={formatStatus(selectedInvoice.status)}
                  color={getStatusColor(selectedInvoice.status) as any}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Vendor</Typography>
                <Typography variant="body1">{selectedInvoice.vendor_name}</Typography>
                <Typography variant="caption" color="text.secondary">GSTIN: {selectedInvoice.vendor_gstin || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">PO Reference</Typography>
                <Typography variant="body1">{selectedInvoice.po_number || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Invoice Date</Typography>
                <Typography variant="body1">
                  {selectedInvoice.invoice_date && new Date(selectedInvoice.invoice_date).toLocaleDateString('en-IN')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Due Date</Typography>
                <Typography variant="body1">
                  {selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString('en-IN') : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Amount Details</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">Gross Amount</Typography>
                <Typography variant="body1">₹{(selectedInvoice.gross_amount || 0).toLocaleString('en-IN')}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">GST Amount</Typography>
                <Typography variant="body1">₹{(selectedInvoice.gst_amount || 0).toLocaleString('en-IN')}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary">TDS Amount</Typography>
                <Typography variant="body1">₹{(selectedInvoice.tds_amount || 0).toLocaleString('en-IN')}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Net Payable</Typography>
                <Typography variant="h6" color="primary.main">₹{(selectedInvoice.net_amount || 0).toLocaleString('en-IN')}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Payment Status</Typography>
                <Chip
                  label={formatStatus(selectedInvoice.payment_status)}
                  color={getPaymentStatusColor(selectedInvoice.payment_status) as any}
                  size="small"
                />
              </Grid>
              {selectedInvoice.approved_by && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">Approved By</Typography>
                    <Typography variant="body1">{selectedInvoice.approved_by}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">Approved At</Typography>
                    <Typography variant="body1">
                      {selectedInvoice.approved_at && new Date(selectedInvoice.approved_at).toLocaleString('en-IN')}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedInvoice?.status === 'PENDING_APPROVAL' && (
            <Button variant="contained" color="success" onClick={() => { handleApproveInvoice(selectedInvoice.id); setViewDialogOpen(false); }}>
              Approve Invoice
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const VendorPayments: React.FC = () => {
  const [payments, setPayments] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [paymentStats, setPaymentStats] = React.useState({
    totalPayments: 0,
    pending: 0,
    processed: 0,
    completed: 0,
    totalAmount: 0,
    totalTDS: 0
  });

  React.useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const { api } = await import('../../services/apiClient');
      const response = await api.get<any>('/procurement/vendor-payments');
      setPayments(response.payments || []);
      setPaymentStats(response.summary || {
        totalPayments: 0,
        pending: 0,
        processed: 0,
        completed: 0,
        totalAmount: 0,
        totalTDS: 0
      });
    } catch (err) {
      console.error('Failed to fetch payments:', err);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'PROCESSED': return 'info';
      case 'PENDING': return 'warning';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  const getPaymentModeLabel = (mode: string) => {
    switch (mode) {
      case 'BANK_TRANSFER': return 'Bank Transfer';
      case 'CHEQUE': return 'Cheque';
      case 'CASH': return 'Cash';
      case 'UPI': return 'UPI';
      default: return mode;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
        Vendor Payments
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Process vendor payments with TDS deductions and generate payment advices.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Payment Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalance color="primary" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="primary.main">
                    {paymentStats.totalPayments}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Payments
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assessment color="warning" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {paymentStats.pending}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assignment color="info" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    {paymentStats.processed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Processed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Assessment color="success" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {paymentStats.completed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalance color="secondary" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="secondary.main">
                    ₹{(paymentStats.totalAmount / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Paid
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ReceiptLong color="error" sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" color="error.main">
                    ₹{(paymentStats.totalTDS / 1000).toFixed(0)}K
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    TDS Deducted
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payments Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Payment Records
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Payment Number</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell align="right">Gross Amount</TableCell>
                <TableCell align="right">TDS</TableCell>
                <TableCell align="right">Net Amount</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {payment.payment_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {payment.vendor_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {payment.vendor_code}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {payment.payment_date && new Date(payment.payment_date).toLocaleDateString('en-IN')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getPaymentModeLabel(payment.payment_mode)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    ₹{(payment.gross_amount || 0).toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell align="right">
                    <Typography color="error">
                      -₹{(payment.tds_amount || 0).toLocaleString('en-IN')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="primary">
                      ₹{(payment.net_amount || 0).toLocaleString('en-IN')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.status}
                      color={getStatusColor(payment.status) as any}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
              {payments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No vendor payments found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

const ProcurementReports: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [financialYear, setFinancialYear] = React.useState('FY2025-26');

  // Report data states
  const [tdsReport, setTdsReport] = React.useState<any>(null);
  const [vendorReport, setVendorReport] = React.useState<any>(null);
  const [purchaseReport, setPurchaseReport] = React.useState<any>(null);
  const [gstReport, setGstReport] = React.useState<any>(null);

  const financialYears = ['FY2025-26', 'FY2024-25', 'FY2023-24'];

  React.useEffect(() => {
    loadReportData();
  }, [activeTab, financialYear]);

  const loadReportData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { api } = await import('../../services/apiClient');

      switch (activeTab) {
        case 0: // TDS Compliance
          const tdsData = await api.get<any>('/procurement/reports/tds-compliance', {
            params: { financialYear }
          });
          setTdsReport(tdsData.report || {});
          break;
        case 1: // Vendor Performance
          const vendorData = await api.get<any>('/procurement/reports/vendor-performance');
          setVendorReport(vendorData.report || {});
          break;
        case 2: // Purchase Analysis
          const purchaseData = await api.get<any>('/procurement/reports/purchase-analysis');
          setPurchaseReport(purchaseData.report || {});
          break;
        case 3: // GST Reconciliation
          const gstData = await api.get<any>('/procurement/reports/gst-reconciliation', {
            params: { financialYear }
          });
          setGstReport(gstData.report || {});
          break;
      }
    } catch (err) {
      console.error('Failed to load report:', err);
      // Set empty state instead of error for missing tables
    } finally {
      setLoading(false);
    }
  };

  const renderTDSComplianceReport = () => {
    if (!tdsReport) return null;
    const { totals, sectionSummary, monthlyTrend, topVendors } = tdsReport;

    return (
      <Box>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary.main">
                  ₹{((totals?.total_tds_deducted || 0) / 100000).toFixed(2)}L
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total TDS Deducted
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  {totals?.certificates_generated || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Certificates Generated
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="warning.main">
                  {totals?.pending_certificates || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Certificates
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="info.main">
                  {totals?.total_transactions || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Section-wise Summary */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Section-wise TDS Summary
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TDS Section</TableCell>
                  <TableCell align="right">Transactions</TableCell>
                  <TableCell align="right">Invoice Amount</TableCell>
                  <TableCell align="right">TDS Deducted</TableCell>
                  <TableCell align="right">Certificates</TableCell>
                  <TableCell align="right">Pending</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(sectionSummary || []).map((section: any) => (
                  <TableRow key={section.tds_section} hover>
                    <TableCell>
                      <Typography fontWeight="medium">Section {section.tds_section}</Typography>
                    </TableCell>
                    <TableCell align="right">{section.transaction_count}</TableCell>
                    <TableCell align="right">₹{(section.total_invoice_amount || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="primary">
                        ₹{(section.total_tds_deducted || 0).toLocaleString('en-IN')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={section.certificates_generated} color="success" size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={section.pending_certificates} color="warning" size="small" />
                    </TableCell>
                  </TableRow>
                ))}
                {(!sectionSummary || sectionSummary.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">No TDS data for selected period</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Top Vendors */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Top Vendors by TDS Deduction
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell>PAN</TableCell>
                  <TableCell align="right">Transactions</TableCell>
                  <TableCell align="right">Invoice Amount</TableCell>
                  <TableCell align="right">TDS Deducted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(topVendors || []).map((vendor: any, index: number) => (
                  <TableRow key={vendor.vendor_id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                          #{index + 1}
                        </Typography>
                        <Typography fontWeight="medium">{vendor.vendor_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{vendor.vendor_pan || '-'}</TableCell>
                    <TableCell align="right">{vendor.transaction_count}</TableCell>
                    <TableCell align="right">₹{(vendor.total_invoice_amount || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="primary">
                        ₹{(vendor.total_tds_deducted || 0).toLocaleString('en-IN')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {(!topVendors || topVendors.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">No vendor data available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderVendorPerformanceReport = () => {
    if (!vendorReport) return null;
    const { summary, topVendors, vendorByType, vendorByState } = vendorReport;

    return (
      <Box>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary.main">
                  {summary?.totalVendors || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Vendors
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  {summary?.activeVendors || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Vendors
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="info.main">
                  ₹{((summary?.totalBusiness || 0) / 100000).toFixed(2)}L
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Business
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="secondary.main">
                  ₹{((summary?.avgBusinessPerVendor || 0) / 1000).toFixed(1)}K
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg per Vendor
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Vendor by Type */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Vendors by Type
                </Typography>
                {(vendorByType || []).map((type: any) => (
                  <Box key={type.vendor_type} sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{type.vendor_type}</Typography>
                      <Box>
                        <Chip label={`${type.vendor_count} total`} size="small" sx={{ mr: 1 }} />
                        <Chip label={`${type.active_count} active`} color="success" size="small" />
                      </Box>
                    </Box>
                  </Box>
                ))}
                {(!vendorByType || vendorByType.length === 0) && (
                  <Typography color="text.secondary">No data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Vendor by State */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Vendors by State (Top 10)
                </Typography>
                {(vendorByState || []).slice(0, 10).map((state: any) => (
                  <Box key={state.state} sx={{ mb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{state.state || 'Unknown'}</Typography>
                      <Chip label={state.vendor_count} size="small" color="primary" variant="outlined" />
                    </Box>
                  </Box>
                ))}
                {(!vendorByState || vendorByState.length === 0) && (
                  <Typography color="text.secondary">No data available</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Top Performing Vendors */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Top Performing Vendors
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">Total Business</TableCell>
                  <TableCell align="right">Avg Order</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(topVendors || []).map((vendor: any, index: number) => (
                  <TableRow key={vendor.vendor_id} hover>
                    <TableCell>#{index + 1}</TableCell>
                    <TableCell>
                      <Typography fontWeight="medium">{vendor.company_name}</Typography>
                    </TableCell>
                    <TableCell>{vendor.vendor_code}</TableCell>
                    <TableCell>{vendor.state || '-'}</TableCell>
                    <TableCell align="right">{vendor.total_orders}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="primary">
                        ₹{(vendor.total_business || 0).toLocaleString('en-IN')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">₹{(vendor.avg_order_value || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      <Chip
                        label={vendor.is_active ? 'Active' : 'Inactive'}
                        color={vendor.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {(!topVendors || topVendors.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary">No vendor performance data</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderPurchaseAnalysisReport = () => {
    if (!purchaseReport) return null;
    const { summary, poByStatus, topProducts, invoiceSummary, paymentSummary } = purchaseReport;

    return (
      <Box>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary.main">
                  {summary?.totalOrders || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Orders
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  ₹{((summary?.totalPOValue || 0) / 100000).toFixed(2)}L
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PO Value
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="info.main">
                  {summary?.totalInvoices || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="secondary.main">
                  ₹{((summary?.totalInvoiceValue || 0) / 100000).toFixed(2)}L
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Invoice Value
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="warning.main">
                  ₹{((summary?.pendingPayments || 0) / 100000).toFixed(2)}L
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Payments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* PO by Status */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Purchase Orders by Status
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Orders</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(poByStatus || []).map((status: any) => (
                      <TableRow key={status.status} hover>
                        <TableCell>
                          <Chip
                            label={status.status}
                            size="small"
                            color={
                              status.status === 'COMPLETED' ? 'success' :
                              status.status === 'PENDING' ? 'warning' :
                              status.status === 'CANCELLED' ? 'error' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">{status.order_count}</TableCell>
                        <TableCell align="right">₹{(status.total_amount || 0).toLocaleString('en-IN')}</TableCell>
                      </TableRow>
                    ))}
                    {(!poByStatus || poByStatus.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography color="text.secondary">No orders found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Payment by Mode */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Payments by Mode
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Mode</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">TDS</TableCell>
                      <TableCell align="right">Net Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(paymentSummary || []).map((payment: any) => (
                      <TableRow key={payment.payment_mode} hover>
                        <TableCell>{payment.payment_mode?.replace('_', ' ')}</TableCell>
                        <TableCell align="right">{payment.payment_count}</TableCell>
                        <TableCell align="right">₹{(payment.total_tds || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="primary">
                            ₹{(payment.total_net || 0).toLocaleString('en-IN')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!paymentSummary || paymentSummary.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="text.secondary">No payment data</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Top Products */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Top Products Purchased
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>HSN Code</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(topProducts || []).map((product: any, index: number) => (
                  <TableRow key={product.product_id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                          #{index + 1}
                        </Typography>
                        <Typography fontWeight="medium">{product.product_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{product.hsn_code || '-'}</TableCell>
                    <TableCell align="right">{product.order_count}</TableCell>
                    <TableCell align="right">{(product.total_quantity || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="primary">
                        ₹{(product.total_value || 0).toLocaleString('en-IN')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {(!topProducts || topProducts.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">No product data available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderGSTReconciliationReport = () => {
    if (!gstReport) return null;
    const { invoiceGST, poGST, totalInvoiceGST, totalPOGST } = gstReport;

    return (
      <Box>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary.main">
                  ₹{((totalInvoiceGST || 0) / 100000).toFixed(2)}L
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  GST on Invoices
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  ₹{((totalPOGST || 0) / 100000).toFixed(2)}L
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  GST on Purchase Orders
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h4" color={Math.abs((totalInvoiceGST || 0) - (totalPOGST || 0)) < 1 ? 'success.main' : 'warning.main'}>
                  ₹{(Math.abs((totalInvoiceGST || 0) - (totalPOGST || 0)) / 1000).toFixed(2)}K
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Variance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Invoice GST */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Monthly Invoice GST
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Invoices</TableCell>
                      <TableCell align="right">Taxable</TableCell>
                      <TableCell align="right">GST</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(invoiceGST || []).map((month: any) => (
                      <TableRow key={month.month} hover>
                        <TableCell>{month.month}</TableCell>
                        <TableCell align="right">{month.invoice_count || 0}</TableCell>
                        <TableCell align="right">₹{(month.taxable_amount || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="primary">
                            ₹{(month.gst_amount || 0).toLocaleString('en-IN')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!invoiceGST || invoiceGST.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="text.secondary">No invoice GST data</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* PO GST */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Monthly PO GST
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Orders</TableCell>
                      <TableCell align="right">Taxable</TableCell>
                      <TableCell align="right">GST</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(poGST || []).map((month: any) => (
                      <TableRow key={month.month} hover>
                        <TableCell>{month.month}</TableCell>
                        <TableCell align="right">{month.order_count || 0}</TableCell>
                        <TableCell align="right">₹{(month.taxable_amount || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="success">
                            ₹{(month.gst_amount || 0).toLocaleString('en-IN')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!poGST || poGST.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="text.secondary">No PO GST data</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const reportTabs = [
    { label: 'TDS Compliance', icon: <ReceiptLong /> },
    { label: 'Vendor Performance', icon: <People /> },
    { label: 'Purchase Analysis', icon: <ShoppingCart /> },
    { label: 'GST Reconciliation', icon: <Assessment /> },
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
        Procurement Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Comprehensive reports for TDS compliance, vendor analysis, and procurement insights.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Financial Year Selector */}
      {(activeTab === 0 || activeTab === 3) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Financial Year
          </Typography>
          <Box display="flex" gap={1}>
            {financialYears.map((fy) => (
              <Chip
                key={fy}
                label={fy}
                color={financialYear === fy ? 'primary' : 'default'}
                variant={financialYear === fy ? 'filled' : 'outlined'}
                onClick={() => setFinancialYear(fy)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Tab Navigation */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', overflowX: 'auto' }}>
            {reportTabs.map((tab, index) => (
              <Button
                key={index}
                startIcon={tab.icon}
                onClick={() => setActiveTab(index)}
                sx={{
                  px: 3,
                  py: 2,
                  borderRadius: 0,
                  borderBottom: activeTab === index ? 2 : 0,
                  borderColor: 'primary.main',
                  color: activeTab === index ? 'primary.main' : 'text.secondary',
                  fontWeight: activeTab === index ? 'bold' : 'normal',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Card>

      {/* Loading State */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 0 && renderTDSComplianceReport()}
          {activeTab === 1 && renderVendorPerformanceReport()}
          {activeTab === 2 && renderPurchaseAnalysisReport()}
          {activeTab === 3 && renderGSTReconciliationReport()}
        </>
      )}
    </Box>
  );
};

const ProcurementPage: React.FC = () => {
  return (
    <Routes>
      <Route index element={<ProcurementCommandCenter />} />
      <Route path="material-requirements" element={<MaterialRequirementsControl />} />
      <Route path="overview" element={<Navigate to="/procurement" replace />} />
      <Route path="exceptions" element={<ProcurementExceptionCenter />} />
      <Route path="requisitions" element={<PurchaseRequisitionPage />} />
      <Route path="requisitions/new" element={<PurchaseRequisitionFormPage />} />
      <Route path="requisitions/:id/edit" element={<PurchaseRequisitionFormPage />} />
      <Route path="requisitions/:id" element={<PurchaseRequisitionDetailPage />} />
      <Route path="rfqs" element={<RfqPage />} />
      <Route path="rfqs/new" element={<RfqFormPage />} />
      <Route path="rfqs/:id/edit" element={<RfqFormPage />} />
      <Route path="rfqs/:id" element={<RfqDetailPage />} />
      <Route path="tds" element={<TDSManagement />} />
      <Route path="orders/:id" element={<PurchaseOrderDetailPage />} />
      <Route path="orders/*" element={<PurchaseOrderPage />} />
      <Route path="vendors/:id" element={<VendorDetailPage />} />
      <Route path="vendors/*" element={<VendorManagementPage />} />
      <Route path="vendor-onboarding" element={<VendorOnboardingPage />} />
      <Route path="vendor-onboarding/:id" element={<VendorOnboardingDetailPage />} />
      <Route path="invoices" element={<PurchaseInvoices />} />
      <Route path="invoices/new" element={<SupplierInvoiceNewPage />} />
      <Route path="invoices/:id" element={<PurchaseInvoiceDetailPage />} />
      <Route path="goods-receipts" element={<GoodsReceiptPage />} />
      <Route path="goods-receipts/new" element={<GoodsReceiptFormPage />} />
      <Route path="goods-receipts/:id" element={<GoodsReceiptDetailPage />} />
      <Route path="service-entries" element={<ServiceEntryPage />} />
      <Route path="service-entries/new" element={<ServiceEntryFormPage />} />
      <Route path="service-entries/:id/edit" element={<ServiceEntryFormPage />} />
      <Route path="service-entries/:id" element={<ServiceEntryDetailPage />} />
      <Route path="service-work-orders" element={<ServiceWorkOrderPage />} />
      <Route path="service-work-orders/new" element={<ServiceWorkOrderFormPage />} />
      <Route path="service-work-orders/:id/edit" element={<ServiceWorkOrderFormPage />} />
      <Route path="service-work-orders/:id" element={<ServiceWorkOrderDetailPage />} />
      <Route path="service-bill-passing" element={<ServiceBillPassingPage />} />
      <Route path="match" element={<MatchWorkbenchPage />} />
      <Route path="payments" element={<VendorPaymentPage />} />
      <Route path="payments/new" element={<VendorPaymentNewPage />} />
      <Route path="payments/proposal" element={<PaymentProposalPage />} />
      <Route path="payments/:id" element={<VendorPaymentDetailPage />} />
      <Route path="analytics" element={<ProcurementAnalyticsPage />} />
      <Route path="reports" element={<ProcurementReports />} />
    </Routes>
  );
};

export default ProcurementPage;